import { useEffect } from "react";
import { FaBox } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  getProductData,
  selectGetProductData,
} from "../slices/get-product-data.slice";

interface PaymentProductInfoProps {
  orderId: string | undefined;
}

export function PaymentProductInfo(props: PaymentProductInfoProps) {
  const { orderId } = props;

  const dispatch = useAppDispatch();
  const getProductDataState = useAppSelector(selectGetProductData);

  const { product_data } = getProductDataState.data ?? {};

  // Fetch product data if not already loaded
  useEffect(() => {
    if (orderId && !getProductDataState.data) {
      dispatch(getProductData({ orderId: orderId }));
    }
  }, [dispatch, orderId, getProductDataState.data]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  if (!product_data || product_data.length === 0) {
    return null;
  }

  return (
    <div className="border rounded-2xl shadow-sm p-5 bg-white">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
        <FaBox className="text-green-600" />
        <span>Product Details</span>
      </h3>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unit Price
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {product_data.map((product, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {product.product_name}
                      </div>
                      {product.product_id && (
                        <div className="text-xs text-gray-500">
                          {product.product_id}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="text-sm text-gray-900">
                    {product.commited_qty || product.order_qty}
                  </span>
                  {product.uom && (
                    <span className="text-xs text-gray-500 ml-1">
                      {product.uom}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                  {formatCurrency(product.productCost || 0)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                  {formatCurrency(
                    (product.commited_qty || product.order_qty) *
                      (product.productCost || 0),
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td
                colSpan={3}
                className="px-4 py-3 text-right text-sm font-semibold text-gray-700"
              >
                Total:
              </td>
              <td className="px-4 py-3 text-right text-lg font-bold text-green-700">
                {formatCurrency(
                  product_data.reduce(
                    (sum, product) =>
                      sum +
                      (product.commited_qty || product.order_qty) *
                        (product.productCost || 0),
                    0,
                  ),
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {product_data.map((product, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-lg p-3 border border-gray-200"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-medium text-gray-900">
                  {product.product_name}
                </p>
                {product.product_id && (
                  <p className="text-xs text-gray-500">{product.product_id}</p>
                )}
              </div>
              <span className="text-sm font-semibold text-green-700">
                {formatCurrency(
                  (product.commited_qty || product.order_qty) *
                    (product.productCost || 0),
                )}
              </span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>
                Qty: {product.commited_qty || product.order_qty}
                {product.uom && ` ${product.uom}`}
              </span>
              <span>@ {formatCurrency(product.productCost || 0)}</span>
            </div>
          </div>
        ))}

        {/* Total */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-green-800">Total Amount:</span>
            <span className="text-xl font-bold text-green-700">
              {formatCurrency(
                product_data.reduce(
                  (sum, product) =>
                    sum +
                    (product.commited_qty || product.order_qty) *
                      (product.productCost || 0),
                  0,
                ),
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
